import React, { FC } from "react";

import Drawer from "antd/es/drawer";
import { defineMessages, useIntl } from "react-intl";

import { GuideStatus } from "@mapmycustomers/shared";
import { Guide, GuideUserStatus } from "@mapmycustomers/shared/types/entity";

import getPageContentContainer from "@app/util/ui/getPageContentContainer";
import useHiddenChat from "@app/util/useHiddenChat";

import UserStats from "../../stats/UserStats";

import styles from "./CompletionDrawer.module.scss";
import UsersTable from "./UsersTable";

const messages = defineMessages({
  subTitle: {
    id: "onboarding.guides.completionDrawer.subTitle",
    defaultMessage: "Guide: {name}",
    description: "SubTitle of a Completion drawer on Guides  page",
  },
  title: {
    id: "onboarding.guides.completionDrawer.title",
    defaultMessage: "Completion Details",
    description: "Title of a Completion drawer on Guides  page",
  },
});

export interface CompletionDrawerProps {
  guide: Guide;
  guideStatus?: GuideUserStatus[];
  onHide: () => void;
}

const CompletionDrawer: FC<CompletionDrawerProps> = ({ guide, guideStatus, onHide }) => {
  const intl = useIntl();
  useHiddenChat();

  const { complete, notStarted } = (guideStatus ?? []).reduce(
    (result, guideStatus) => ({
      complete: result.complete + (guideStatus.status === GuideStatus.COMPLETED ? 1 : 0),
      notStarted: result.notStarted + (guideStatus.status === GuideStatus.NOT_STARTED ? 1 : 0),
    }),
    { complete: 0, notStarted: 0 }
  );

  return (
    <Drawer
      className={styles.container}
      closable
      getContainer={getPageContentContainer()}
      mask
      onClose={onHide}
      placement="right"
      push={{ distance: 0 }}
      title={
        <div className={styles.header}>
          <div>{intl.formatMessage(messages.title)}</div>
          <div className={styles.subTitle}>
            {intl.formatMessage(messages.subTitle, { name: guide.displayName })}
          </div>
        </div>
      }
      visible
      width={450}
    >
      <div className={styles.stats}>
        <UserStats
          className={styles.item}
          count={complete}
          size="small"
          total={guideStatus?.length ?? 0}
          type="completed"
        />
        <UserStats
          className={styles.item}
          count={notStarted}
          size="small"
          total={guideStatus?.length ?? 0}
          type="notStarted"
        />
      </div>
      <UsersTable className={styles.users} guideStatus={guideStatus} />
    </Drawer>
  );
};

export default CompletionDrawer;
